import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  addPolicyVersion,
  editPolicyVersion,
  getPolicyById,
  getPolicyVersionByPolicyIdAndVersion,
  updatePolicyVersionAttachment,
} from 'services/policies-service';
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { VersionFormComponent } from './Components/Form';
import { Main as Container } from 'Components/Main';
import { FormSuccess } from 'Components/Form/Success';
import { useTranslation } from 'react-i18next';
import { Dialog, PageHeader, Button } from 'Components/ui';
import { isPolicyVersionEditable, policyWorkflowErrorMessage } from 'utils/policy_version_utils';

/* ── Styled Components ── */

const PageWrap = styled.div`
  padding-top: 24px;
  padding-bottom: 32px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;


/* ── Component ── */

export const PolicyVersionForm = () => {
  const { t } = useTranslation(['policy']);
  const { id, idVersion } = useParams();
  const navigate = useNavigate();
  const [finished, setFinished] = useState(false);
  const [idPolicyVersion, setIdPolicyVersion] = useState(null);
  const [existingData, setExistingData] = useState(null);
  // Edit route guard: null = still checking, false = version is past draft and may not be edited.
  const [editAllowed, setEditAllowed] = useState(idVersion ? null : true);

  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    // Content fields only — status and workflow dates are never edited through this form.
    defaultValues: {
      approver: null,
      author: null,
      policyText: '',
      reviewer: null,
      version: null,
      attachment: null,
    },
  });

  const idemRef = useRef(null);

  const { data: policy } = useQuery(
    ['getPolicyById', id],
    async () => {
      const res = await getPolicyById(id);
      return res.status === 200 ? res.data : null;
    },
    { enabled: !!id }
  );

  const mutation = useMutation(
    async (normalizedPayload) => {
      if (idVersion) {
        return editPolicyVersion(id, idVersion, normalizedPayload);
      }
      if (!idemRef.current) {
        idemRef.current = (window.crypto?.randomUUID?.()) ?? `${Date.now()}-${Math.random()}`;
      }
      return addPolicyVersion(id, { ...normalizedPayload, idempotencyKey: idemRef.current });
    },
    {
      onError: (error) => {
        Dialog.fire({
          title: t('policy:versionForm.swal.errorTitle'),
          html: policyWorkflowErrorMessage(t, error) || error.message,
          icon: 'error',
        });
      },
      onSuccess: (res) => {
        if (res?.status === 200) {
          setIdPolicyVersion(idVersion);
        } else if (res?.status === 201) {
          setIdPolicyVersion(res?.data?.policyVersionId);
        }
        setFinished(true);
      },
      onSettled: () => {
        idemRef.current = null;
      },
    }
  );

  const handleComplete = async (normalizedPayload) => {
    await mutation.mutateAsync(normalizedPayload);
  };

  const attachmentMutation = useMutation(
    async (newFile) => {
      if (!idVersion) throw new Error('Versão da política não encontrada para atualizar anexo');
      return updatePolicyVersionAttachment(id, idVersion, newFile);
    },
    {
      onError: (error) => {
        Dialog.fire({
          title: t('policy:versionForm.swal.errorTitle'),
          html: error?.response?.data?.message || error.message,
          icon: 'error',
        });
      },
      onSuccess: () => {
        Dialog.alert({
          title: t('policy:versionForm.swal.successTitle'),
          message: t('policy:versionForm.attachedDoc.swapSuccess'),
          icon: 'success',
        }).then(() => window.location.reload());
      },
    }
  );

  const handleUpdateAttachment = async (newFile) => {
    await attachmentMutation.mutateAsync(newFile);
  };

  const handleBack = () => navigate(`/policies/show/${id}`);

  const getPolicyVersion = useCallback(async () => {
    if (id && idVersion) {
      try {
        const resPolicy = await getPolicyVersionByPolicyIdAndVersion(id, idVersion);
        const data = resPolicy.data;
        if (!isPolicyVersionEditable(data)) {
          setEditAllowed(false);
          await Dialog.alert({
            title: t('policy:versions.notEditable.title'),
            message: t('policy:versions.notEditable.text'),
            icon: 'warning',
          });
          navigate(`/policies/show/${id}/versions/${idVersion}`, { replace: true });
          return;
        }
        setEditAllowed(true);
        setExistingData(data);
        ['version', 'policyText', 'author', 'reviewer', 'approver', 'attachment'].forEach(key =>
          setValue(key, data[key] ?? null)
        );
      } catch (error) {
        Dialog.alert({
          title: t('policy:versionForm.swal.errorTitle'),
          message: error?.response?.data?.message ?? error.message,
          icon: 'error',
        });
      }
    }
  }, [id, idVersion, setValue, t, navigate]);

  useEffect(() => {
    getPolicyVersion();
  }, [getPolicyVersion]);

  const isLoading = mutation.isLoading || attachmentMutation.isLoading;

  return (
    <Container>
      <PageWrap>
        {!finished && editAllowed && (
          <>
            <PageHeader
              title={t(idVersion ? 'policy:versions.titleEdit' : 'policy:versions.titleAdd')}
              segments={[
                { label: t('common:breadcrumbs.compliance') },
                { label: t('policy:page.title'), route: '/policies' },
                { label: policy?.name ?? '', route: `/policies/show/${id}` },
                { label: t(idVersion ? 'policy:versions.titleEdit' : 'policy:versions.titleAdd') },
              ]}
              actions={
                <HeaderActions>
                  <Button variant="outline" type="button" onClick={handleBack} disabled={isLoading} sx={{ height: '44px', padding: '0 20px' }}>
                    {t('common:buttons.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    form="version-form"
                    type="submit"
                    disabled={isLoading}
                    isLoading={isLoading}
                    sx={{ height: '44px', padding: '0 24px', minWidth: '100px' }}
                  >
                    {t('policy:versionForm.save')}
                  </Button>
                </HeaderActions>
              }
            />
            <VersionFormComponent
              control={control}
              errors={errors}
              getValues={getValues}
              handleBack={handleBack}
              handleComplete={handleComplete}
              handleUpdateAttachment={handleUpdateAttachment}
              handleSubmit={handleSubmit}
              id={id}
              isLoading={isLoading}
              register={register}
              setValue={setValue}
              existingData={existingData}
              policyName={policy?.name}
            />
          </>
        )}
        {mutation.isSuccess && finished && (
          <FormSuccess
            title={t('policy:versions.successMessage')}
            labelStartAgain={t('policy:versions.viewDetails')}
            handleStartAgain={() => navigate(`/policies/show/${id}/versions/${idPolicyVersion}`)}
            showButtonSeeDetail={false}
          />
        )}
      </PageWrap>
    </Container>
  );
};
